import { Flex } from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { ReceiptSelector } from './ReceiptSelector';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import {
  MenuTree,
  MenuTreeItem,
} from '~/packages/common-frontend/components/MenuTree';

// @dani need to spin these up inside the component now based on the what portcovs exist
type DigitalMonthlyReportPresetTab =
  | 'facilitySummary'
  | 'borrowingBase'
  | 'borrowingBaseReceivables'
  | 'borrowingBaseCash'
  | 'borrowingBaseSummary'
  | 'portfolioCovenant'
  | 'financialCovenant'
  | 'inputs'
  | 'monthlyStatements';

const borrowingBaseReportTabs = [
  'borrowingBase',
  'borrowingBaseReceivables',
  'borrowingBaseCash',
  'borrowingBaseSummary',
] as const;
export type BorrowingBaseReportTab = typeof borrowingBaseReportTabs[number];

export const isBorrowingBaseReportTab = (
  test: any,
): test is BorrowingBaseReportTab => {
  return test && _.isString(test) && _.includes(borrowingBaseReportTabs, test);
};

export type DigitalMonthlyReportTab = DigitalMonthlyReportPresetTab | string;

const tabsToLabels: Record<DigitalMonthlyReportTab, string> = {
  facilitySummary: 'Facility Summary',
  borrowingBase: 'Borrowing Base',
  borrowingBaseSummary: 'Summary',
  borrowingBaseReceivables: 'Receivables',
  borrowingBaseCash: 'Cash',
  monthlyStatements: 'Monthly Statements',
  portfolioCovenant: 'Portfolio Covenant',
  financialCovenant: 'Financial Covenant',
  inputs: 'Inputs',
};

const DigitalMonthlyReportSidebarMenuButton = ({
  tab,
  selectedTab,
  onClick,
  children,
}: {
  tab: DigitalMonthlyReportTab;
  selectedTab: DigitalMonthlyReportTab;
  onClick: () => void;
  children?: React.ReactNode;
}) => (
  <MenuTreeItem
    colorScheme="gray"
    label={tabsToLabels[tab] ?? commonUtils.toTitleCase(tab)}
    isActive={tab === selectedTab}
    fullSurfaceExpandsContent={false}
    ButtonProps={{ onClick }}
  >
    {children}
  </MenuTreeItem>
);

const DigitalMonthlyReportSidebarSubMenuButton = ({
  tab,
  selectedTab,
  onClick,
}: {
  tab: DigitalMonthlyReportTab;
  selectedTab: DigitalMonthlyReportTab;
  onClick: () => void;
}) => (
  <MenuTreeItem
    colorScheme="gray"
    size="sm"
    label={tabsToLabels[tab] ?? commonUtils.toTitleCase(tab)}
    isActive={tab === selectedTab}
    overflow="multi-line"
    ButtonProps={{ onClick }}
  />
);

type DigitalMonthlyReportSidebarProps = {
  selectedTab: DigitalMonthlyReportTab;
  setSelectedTab: React.Dispatch<React.SetStateAction<DigitalMonthlyReportTab>>;
  portfolioCovenantKeys: string[] | null;
  financialCovenantKeys: string[] | null;
  borrowingBaseReportId: string | null;
  hasMonthlyStatements: boolean;
};
export const DigitalMonthlyReportSidebar = ({
  selectedTab,
  setSelectedTab,
  portfolioCovenantKeys,
  financialCovenantKeys,
  borrowingBaseReportId,
  hasMonthlyStatements,
}: DigitalMonthlyReportSidebarProps) => {
  const flags = useFeatureFlags();
  return (
    <Flex direction="column" gap="1.5rem">
      <ReceiptSelector />
      <MenuTree
        FlexProps={{ minWidth: '18.75rem', flexShrink: 0, width: '100%' }}
      >
        <DigitalMonthlyReportSidebarMenuButton
          tab="facilitySummary"
          selectedTab={selectedTab}
          onClick={() => setSelectedTab('facilitySummary')}
        />

        {/* Monthly Statements Group */}
        {hasMonthlyStatements && (
          <DigitalMonthlyReportSidebarMenuButton
            tab="monthlyStatements"
            selectedTab={selectedTab}
            onClick={() => setSelectedTab('monthlyStatements')}
          />
        )}

        {/* Borrowing Base Group */}
        {borrowingBaseReportId && (
          <DigitalMonthlyReportSidebarMenuButton
            tab="borrowingBase"
            selectedTab={selectedTab}
            onClick={() => setSelectedTab('borrowingBase')}
          >
            <DigitalMonthlyReportSidebarSubMenuButton
              tab="borrowingBaseSummary"
              selectedTab={selectedTab}
              onClick={() => setSelectedTab('borrowingBaseSummary')}
            />
            <DigitalMonthlyReportSidebarSubMenuButton
              tab="borrowingBaseReceivables"
              selectedTab={selectedTab}
              onClick={() => setSelectedTab('borrowingBaseReceivables')}
            />
            <DigitalMonthlyReportSidebarSubMenuButton
              tab="borrowingBaseCash"
              selectedTab={selectedTab}
              onClick={() => setSelectedTab('borrowingBaseCash')}
            />
          </DigitalMonthlyReportSidebarMenuButton>
        )}

        {/* Portfolio Covenant Group */}
        {flags.displayPortfolioCovenants && !!portfolioCovenantKeys?.length && (
          <DigitalMonthlyReportSidebarMenuButton
            tab="portfolioCovenant"
            selectedTab={selectedTab}
            onClick={() => setSelectedTab('portfolioCovenant')}
          >
            {portfolioCovenantKeys.map((covenantKey) => (
              <DigitalMonthlyReportSidebarSubMenuButton
                key={covenantKey}
                tab={covenantKey}
                selectedTab={selectedTab}
                onClick={() => setSelectedTab(covenantKey)}
              />
            ))}
          </DigitalMonthlyReportSidebarMenuButton>
        )}

        {/* Financial Covenant Group */}
        {flags.displayFinancialCovenantInsights &&
          !!financialCovenantKeys?.length && (
            <DigitalMonthlyReportSidebarMenuButton
              tab="financialCovenant"
              selectedTab={selectedTab}
              onClick={() => setSelectedTab('financialCovenant')}
            >
              {financialCovenantKeys.map((covenantKey) => (
                <DigitalMonthlyReportSidebarSubMenuButton
                  key={covenantKey}
                  tab={covenantKey}
                  selectedTab={selectedTab}
                  onClick={() => setSelectedTab(covenantKey)}
                />
              ))}
            </DigitalMonthlyReportSidebarMenuButton>
          )}

        <DigitalMonthlyReportSidebarMenuButton
          tab="inputs"
          selectedTab={selectedTab}
          onClick={() => setSelectedTab('inputs')}
        />
      </MenuTree>
    </Flex>
  );
};
